import { CompanyProfileInsight, insightConfidencePercent } from '../company-profile-template/profile-insight';
import { isTaxProfilePlay, TaxProfilePlay } from './tax-profile-presenters';

/** One Magic Insights question answered for the company, e.g. "Research and development" or "Patent Box". */
export interface TaxInsightGroup {
  id: string;
  label: string;
  response: any;
  confidence: number | null;
  unknown: boolean;
  referenceCount: number;
  generated: string;
  cards: CompanyProfileInsight[];
}

/** The answers for one service tab from tax_profile_insights. */
export interface TaxInsightPlay {
  id: TaxProfilePlay;
  label: string;
  questionCount: number;
  groups: TaxInsightGroup[];
  blocks: TaxInsightBlock[];
}

export type TaxInsightPlays = Partial<Record<TaxProfilePlay, TaxInsightPlay>>;

const COMPACT_TYPES: Record<string, string> = {
  gauge: 'likelihood',
  number: 'amount',
  card_list: 'records',
  pills: 'chips',
  string_list: 'chips',
  labelled_pills: 'notes',
  vertical_bar_chart: 'bars',
  pie_chart: 'breakdown',
  string: 'text',
};

// Headline estimates first, then summaries, then the supporting lists.
const TYPE_ORDER: Record<string, number> = { likelihood: 0, amount: 1, text: 2, bars: 3, breakdown: 3, records: 4, notes: 5, chips: 6 };

/** Maps a platform display type onto the compact Tax renderer. */
export function taxInsightDisplayType(schema: any, value: unknown): string {
  const type = COMPACT_TYPES[schema?.display_type];
  if (type) return type;
  if (typeof value === 'number') return 'amount';
  if (Array.isArray(value)) return value.some(item => item && typeof item === 'object') ? 'records' : 'chips';
  return 'text';
}

/**
 * "R&D Tax Relief - Future Claim Likelihood" reads as "Future claim likelihood" under its group heading.
 * Acronyms such as UK, R&D and EII/CCL keep their capitals.
 */
export function taxInsightTitle(label: unknown): string {
  const text = String(label || '').replace(/_/g, ' ').trim();
  const tail = text.includes(' - ') ? text.split(' - ').pop()!.trim() : text;
  const words = tail.split(/\s+/).filter(Boolean).map((word, index) => {
    const lower = /^[A-Z][a-z]+$/.test(word) ? word.toLowerCase() : word;
    return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
  });
  return words.join(' ').replace(/\badvisor(s?)\b/gi, (_, plural) => `adviser${plural}`);
}

function emptyText(title: string): string {
  const lowered = /^[A-Z][a-z]/.test(title) ? title.charAt(0).toLowerCase() + title.slice(1) : title;
  return `No ${lowered} recorded`;
}

function eyebrowFor(type: string, schema: any): string {
  const estimated = schema?.prepend === '£' || !!schema?.append || /estimat/i.test(String(schema?.label || ''));
  if (type === 'likelihood' || (type === 'amount' && estimated)) return 'AI ESTIMATE';
  if (type === 'text') return 'AI SUMMARY';
  return 'AI FINDINGS';
}

function isMissing(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && !value.trim()) ||
    (!!value && typeof value === 'object' && !Array.isArray(value) && !Object.keys(value).length);
}

export function taxInsightCards(response: any): CompanyProfileInsight[] {
  const schema = response?.display_schema;
  const properties = schema?.properties && typeof schema.properties === 'object' ? schema.properties : null;
  const entries: Array<{ key: string; schema: any; value: unknown }> = properties
    ? Object.entries(properties).map(([key, field]) => ({ key, schema: field, value: response?.answer?.[key] }))
    : [{ key: String(response?.question_data_api_key || 'answer'), schema, value: response?.answer }];
  return entries
    .filter(entry => !isMissing(entry.value))
    .map((entry, index) => {
      const displayType = taxInsightDisplayType(entry.schema, entry.value);
      const title = taxInsightTitle(entry.schema?.label || entry.key);
      return {
        key: entry.key,
        title,
        eyebrow: eyebrowFor(displayType, entry.schema),
        schema: entry.schema || {},
        value: entry.value,
        displayType,
        response,
        emptyText: emptyText(title),
        order: index,
      };
    })
    .sort((a, b) =>
      Number(Array.isArray(a.value) && !a.value.length) - Number(Array.isArray(b.value) && !b.value.length) ||
      (TYPE_ORDER[a.displayType] ?? 7) - (TYPE_ORDER[b.displayType] ?? 7) ||
      a.order - b.order
    );
}

/**
 * Grid spans on a 12-column row: headline estimates take a quarter, lists and summaries half. The last card in
 * each row stretches to fill it, so a group never leaves a gap at the end of a row.
 */
export function taxInsightSpans(cards: Array<{ displayType: string }>): number[] {
  const spans = cards.map(card => ['likelihood', 'amount'].includes(card.displayType) ? 3 : 6);
  let used = 0;
  spans.forEach((span, index) => {
    if (used + span > 12) {
      spans[index - 1] += 12 - used;
      used = 0;
    }
    used += span;
  });
  if (spans.length && used < 12) spans[spans.length - 1] += 12 - used;
  return spans;
}

export function taxInsightGroup(response: any): TaxInsightGroup {
  const key = String(response?.question_data_api_key || response?.question_id || '');
  return {
    id: key,
    label: String(response?.label || '').trim() || taxInsightTitle(key) || 'Magic Insights answer',
    response,
    confidence: insightConfidencePercent(response?.confidence),
    unknown: !!response?.is_unknown,
    referenceCount: Array.isArray(response?.references) ? response.references.length : 0,
    generated: String(response?.date_generated || ''),
    cards: withSpans(response?.is_unknown ? [] : taxInsightCards(response)),
  };
}

function withSpans(cards: CompanyProfileInsight[]): CompanyProfileInsight[] {
  const spans = taxInsightSpans(cards);
  return cards.map((card, index) => ({ ...card, span: spans[index] }));
}

/** A run of answers laid out together: one multi-card question, or consecutive single-card questions in one grid. */
export interface TaxInsightBlock {
  id: string;
  group: TaxInsightGroup | null;
  singles: Array<{ group: TaxInsightGroup; card: CompanyProfileInsight & { span: number } }>;
}

/**
 * Questions with several fields keep their own heading. Consecutive questions that produced a single card share a
 * grid, with the confidence on each card, so a run of one-line answers does not become a tall stack of headings.
 */
export function taxInsightBlocks(groups: TaxInsightGroup[]): TaxInsightBlock[] {
  const blocks: TaxInsightBlock[] = [];
  for (const group of groups) {
    if (group.cards.length !== 1 || group.unknown) {
      blocks.push({ id: group.id, group, singles: [] });
      continue;
    }
    let block = blocks[blocks.length - 1];
    if (!block || block.group) {
      block = { id: `singles-${group.id}`, group: null, singles: [] };
      blocks.push(block);
    }
    block.singles.push({ group, card: { ...group.cards[0], span: 0 } });
  }
  for (const block of blocks) {
    const spans = taxInsightSpans(block.singles.map(single => single.card));
    block.singles.forEach((single, index) => single.card.span = spans[index]);
  }
  return blocks;
}

/** Groups the endpoint's plays by tab. The "other" play has no tab yet and is left out. */
export function taxInsightPlays(payload: unknown): TaxInsightPlays {
  const plays: TaxInsightPlays = {};
  if (!Array.isArray(payload)) return plays;
  for (const play of payload) {
    if (!isTaxProfilePlay(play?.id)) continue;
    const responses = Array.isArray(play.responses) ? play.responses : [];
    const groups = responses.filter((response: any) => response && typeof response === 'object').map(taxInsightGroup);
    plays[play.id] = {
      id: play.id,
      label: String(play.label || ''),
      questionCount: Number(play.question_count) || 0,
      groups,
      blocks: taxInsightBlocks(groups),
    };
  }
  return plays;
}
